// Timestamps (DESIGN_SPEC §5, §12.16): every timestamp is a <time datetime> with the full UTC instant in its title.
//   <Time value t0? mode? className?>{children?}</Time>
//     value: ISO string, epoch ms or Date · children override the text
//     t0 given → T0-relative text: "+00:02.930", "−00:07.700", "+1:02:41" past an hour
//     otherwise mode: "clock" 06:51:31 (default) · "ms" 06:51:31.860 · "date" Oct 11 23:26 · "iso"
//   relText(ms), utcText(date, mode)   the same formatting as plain strings
import type { ReactNode } from "react";

type TimeMode = "clock" | "ms" | "date" | "iso";
type Instant = string | number | Date;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n: number, w = 2) => String(n).padStart(w, "0");

function toDate(v: Instant | null | undefined): Date | null {
  if (v === null || v === undefined || v === "") return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function utcText(d: Date, mode: TimeMode = "clock"): string {
  const hms = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  if (mode === "ms") return `${hms}.${pad(d.getUTCMilliseconds(), 3)}`;
  if (mode === "date") return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} ${hms.slice(0, 5)}`;
  if (mode === "iso") return d.toISOString();
  return hms;
}

function relText(ms: number): string {
  const sign = ms < 0 ? "−" : "+";
  const a = Math.round(Math.abs(ms));
  const h = Math.floor(a / 3_600_000);
  const m = Math.floor(a / 60_000) % 60;
  const s = Math.floor(a / 1000) % 60;
  if (h > 0) return `${sign}${h}:${pad(m)}:${pad(s)}`;
  return `${sign}${pad(m)}:${pad(s)}.${pad(a % 1000, 3)}`;
}

export function Time({
  value,
  t0,
  mode = "clock",
  className,
  children,
}: {
  value: Instant;
  t0?: Instant | null;
  mode?: TimeMode;
  className?: string;
  children?: ReactNode;
}) {
  const d = toDate(value);
  if (!d) return <span className={className}>—</span>;
  const iso = d.toISOString();
  const base = toDate(t0);
  const text = children ?? (base ? relText(d.getTime() - base.getTime()) : utcText(d, mode));
  return (
    <time dateTime={iso} title={`${iso.replace("T", " ").replace("Z", "")} UTC`} className={className}>
      {text}
    </time>
  );
}
